/** All money is in rupees. 1 lakh = 1e5, 1 crore = 1e7. */
export const LAKH = 1e5;
export const CRORE = 1e7;

export type BucketKey = "life" | "school" | "undergrad" | "elder" | "health" | "access";

/** Household shape at the retirement date. */
export interface Household {
  /** Age of the older adult in the retirement year. */
  adultAge: number;
  /** Age of each child in the retirement year. Empty array for none. */
  childAges: number[];
  /** Number of semi-dependent parents supported. 0 disables the elder bucket. */
  parentCount: number;
  /** Average age of those parents in the retirement year. */
  parentAge: number;
}

/** Annual spending, in TODAY's rupees, before inflation and before tax. */
export interface Spending {
  /** Core lifestyle only. Excludes everything below. */
  lifestylePerYear: number;
  /**
   * What `lifestylePerYear` is denominated in.
   *
   * false (default): Bengaluru prices, and the model adjusts it for the chosen
   * city. This is what the slider means.
   * true: already priced for the chosen city — set when the figure comes from
   * the expense planner, which prices every line locally. Adjusting it again
   * would discount a cheap town twice.
   */
  lifestylePricedLocally: boolean;
  /** All-in schooling per child per year: fees, transport, activities, tutoring. */
  schoolPerChildPerYear: number;
  /** Undergraduate cost per child per year, and how many years it runs. */
  undergradPerYear: number;
  undergradYears: number;
  /** Age at which a child starts undergrad (school runs until the year before). */
  undergradStartAge: number;
  /** Health cover for the nuclear family, per year. */
  healthCoverPerYear: number;
  /**
   * Elder-care ramp: cost per year for ALL supported parents combined, keyed by
   * the parents' age. Steps apply from the listed age until the next step.
   */
  elderRamp: { fromAge: number; perYear: number }[];
  /** Age past which elder care is assumed to end. */
  elderEndAge: number;
  /**
   * Cost index for the retirement city, 1.0 = Bengaluru.
   * Applied only to the locally-priced share of the basket.
   */
  cityIndex: number;
  /** Share of the lifestyle basket that is locally priced (rest is global). */
  localShare: number;
  /** Which city profile this plan is built on. See cities.ts. */
  cityId: string;
  /**
   * Cost per year, today's rupees, of reaching services the town does not have:
   * specialist trips to the nearest metro and the travel around them. Zero in a
   * metro. Scaled by household size, and inflated at the medical rate, because
   * that is what actually drives it.
   */
  remoteAccessPerYear: number;
}

/** Inflation applied to each bucket. Annual, nominal. */
export interface Inflation {
  general: number;
  generalVol: number;
  /** Correlation between an inflation surprise and the equity return. */
  equityCorrelation: number;
  life: number;
  school: number;
  undergrad: number;
  /** Used for both elder care and health cover. */
  medical: number;
}

/** Four assets: Indian equity, global equity in INR, Indian debt, gold. */
export interface Markets {
  /** Arithmetic mean nominal return, per asset. */
  mu: [number, number, number, number];
  /** Annual volatility, per asset. */
  sigma: [number, number, number, number];
  /** 4x4 correlation matrix. */
  correlation: number[][];
  /** Allocation at retirement, and at the end of the glide. Must each sum to 1. */
  allocStart: [number, number, number, number];
  allocEnd: [number, number, number, number];
  /** Years over which the allocation glides from start to end. */
  glideYears: number;
  /**
   * Share of each asset's return taken by tax EVERY year, whether or not you
   * spend it. Fixed deposits work this way: interest is taxed at slab as it is
   * earned. Funds do not — they are taxed only on the gain in what you actually
   * withdraw, which is what `taxDrag` models. Getting this wrong is the single
   * largest error available in this model: an FD portfolio taxed as if it were
   * a fund looks about 40% cheaper than it is.
   */
  annualTax: [number, number, number, number];
  /** Which allocation preset this came from, so the UI can show it. */
  presetId: string;
}

export interface PlanInputs {
  household: Household;
  spending: Spending;
  inflation: Inflation;
  markets: Markets;
  /**
   * Effective tax paid on a gross withdrawal, as a fraction of that withdrawal.
   * A mature SWP is mostly return of capital, so this is well below the
   * headline capital-gains rate.
   */
  taxDrag: number;
  /** Years the money must last. 50 takes a 45-year-old to about 95. */
  horizonYears: number;
  /** Years between today's prices and the retirement date. */
  yearsToRetirement: number;
  /** Cut discretionary spending when the withdrawal rate drifts above plan. */
  guardrails: boolean;
  /** Number of Monte Carlo paths. */
  simulations: number;
  /** Seed, so a shared link reproduces the same numbers. */
  seed: number;
}

export interface SolveResult {
  /** Corpus needed at each confidence level, in rupees. */
  corpus: Record<string, number>;
  /** Gross outflow in year one, including tax. */
  yearOneGross: number;
  /** Corpus at the primary confidence level divided by yearOneGross. */
  multiple: number;
  /** yearOneGross / corpus at the primary confidence level. */
  impliedSwr: number;
  /** What the plan costs if returns simply arrive on average. */
  deterministic: number;
  /** How much more the primary target is than the deterministic cost. */
  sequenceRiskPremium: number;
  /** Per-bucket cost, discounted at the portfolio's own mean return. */
  bucketCost: Record<BucketKey, number>;
  /** Annual outflow by bucket, deflated to retirement-year rupees. */
  realOutflow: Record<BucketKey, number[]>;
  /** Milliseconds the solve took. */
  elapsedMs: number;
}
